import { motion } from "framer-motion";
import { useCAPartner, TIER_THRESHOLDS, COMMISSION_RATES } from "@/contexts/CAPartnerContext";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import {
  Users, Wallet, TrendingUp, IndianRupee, Award,
  Building2, CreditCard, CheckCircle2, Clock, AlertCircle, Crown,
} from "lucide-react";
import PartnerOnboardForm from "@/components/partner/PartnerOnboardForm";
import PartnerCommissionTable from "@/components/partner/PartnerCommissionTable";
import PartnerPayoutSection from "@/components/partner/PartnerPayoutSection";
import { MonthlyCommissionChart, ClientGrowthChart, PlanDistributionChart, RevenueContributionChart } from "@/components/partner/PartnerAnalyticsCharts";

const Card = ({ children, className = "" }: { children: React.ReactNode; className?: string }) => (
  <div className={`p-5 rounded-lg bg-card border border-border shadow-card ${className}`}>{children}</div>
);

const clientStatusCfg = {
  active: { label: "Active", color: "bg-success/10 text-success border-success/20", icon: CheckCircle2 },
  trial: { label: "Trial", color: "bg-accent/10 text-accent border-accent/20", icon: Clock },
  churned: { label: "Churned", color: "bg-destructive/10 text-destructive border-destructive/20", icon: AlertCircle },
};

export default function CAPartnerDashboardPage() {
  const { profile, clients, stats } = useCAPartner();
  const tierInfo = TIER_THRESHOLDS[profile.tier];
  const nextTier = profile.tier === "silver" ? "gold" : profile.tier === "gold" ? "platinum" : null;
  const nextThreshold = nextTier ? TIER_THRESHOLDS[nextTier].minClients : profile.clientCount;
  const tierProgress = nextTier ? Math.min(100, Math.round((profile.clientCount / nextThreshold) * 100)) : 100;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold font-display text-foreground">CA Partner Program</h1>
          <p className="text-sm text-muted-foreground">Onboard clients to AccouPro and earn recurring commissions</p>
        </div>
        <Badge variant="outline" className={`text-sm px-3 py-1 ${tierInfo.color} flex items-center gap-1.5`}>
          <Crown className="h-3.5 w-3.5" /> {tierInfo.label} — {Math.round(profile.commissionRate * 100)}% Commission
        </Badge>
      </div>

      {/* Tier Progress */}
      {nextTier && (
        <Card className="border-accent/30 bg-gradient-to-br from-accent/5 to-transparent">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-10 h-10 rounded-xl bg-accent/10 flex items-center justify-center">
              <Award className="h-5 w-5 text-accent" />
            </div>
            <div className="flex-1">
              <div className="flex items-center justify-between flex-wrap gap-1">
                <p className="text-sm font-semibold text-foreground">
                  Progress to {TIER_THRESHOLDS[nextTier].label}
                </p>
                <span className="text-xs text-muted-foreground">
                  {profile.clientCount}/{nextThreshold} clients — unlocks {Math.round(COMMISSION_RATES[nextTier] * 100)}% commission
                </span>
              </div>
              <Progress value={tierProgress} className="h-2 mt-2" />
            </div>
          </div>
        </Card>
      )}

      {/* Stats Grid — 6 cards */}
      <div className="grid grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-3">
        {[
          { label: "Total Companies", value: stats.totalClients, icon: Building2, color: "text-accent" },
          { label: "Active Clients", value: stats.activeClients, icon: Users, color: "text-success" },
          { label: "Monthly Commission", value: `₹${stats.monthlyRecurringCommission}`, icon: TrendingUp, color: "text-success" },
          { label: "Total Earned", value: `₹${stats.totalEarned}`, icon: IndianRupee, color: "text-accent" },
          { label: "Pending Payout", value: `₹${stats.pendingBalance}`, icon: Wallet, color: "text-warning" },
          { label: "Churn Rate", value: `${stats.churnRate}%`, icon: AlertCircle, color: "text-destructive" },
        ].map((stat, i) => (
          <motion.div key={i} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.04 }}>
            <Card>
              <div className="flex items-center justify-between mb-2">
                <span className="text-[11px] text-muted-foreground">{stat.label}</span>
                <stat.icon className={`h-4 w-4 ${stat.color}`} />
              </div>
              <div className="text-xl font-bold font-display text-foreground">{stat.value}</div>
            </Card>
          </motion.div>
        ))}
      </div>

      {/* Charts Row 1 */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <MonthlyCommissionChart />
        <ClientGrowthChart />
      </div>

      {/* Charts Row 2 */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <PlanDistributionChart />
        <RevenueContributionChart />
      </div>

      {/* Commission Structure */}
      <Card>
        <h3 className="text-sm font-semibold text-foreground mb-3">Commission Structure</h3>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          {(["starter", "professional", "enterprise"] as const).map((plan) => {
            const prices = { starter: 499, professional: 999, enterprise: 1999 };
            const commission = Math.round(prices[plan] * profile.commissionRate);
            const icons = { starter: CreditCard, professional: Building2, enterprise: Crown };
            const Icon = icons[plan];
            return (
              <div key={plan} className="p-3 rounded-lg bg-muted/20 border border-border">
                <Icon className="h-5 w-5 text-accent mb-2" />
                <p className="text-sm font-semibold text-foreground capitalize">{plan} Plan — ₹{prices[plan]}/mo</p>
                <p className="text-xs text-muted-foreground mt-0.5">
                  Your commission: <span className="font-semibold text-success">₹{commission}/mo</span>
                </p>
              </div>
            );
          })}
        </div>
      </Card>

      {/* Onboard Client */}
      <PartnerOnboardForm />

      {/* Clients Table */}
      <Card>
        <h3 className="text-sm font-semibold text-foreground mb-3 flex items-center gap-2">
          <Users className="h-4 w-4 text-accent" /> Your Clients ({clients.length})
        </h3>
        <div className="space-y-2 max-h-[320px] overflow-y-auto pr-1">
          {clients.map((client) => {
            const cfg = clientStatusCfg[client.status];
            const StatusIcon = cfg.icon;
            const monthlyComm = Math.round(client.monthlyAmount * client.commissionRate);
            return (
              <div key={client.id} className="flex items-center justify-between p-3 rounded-lg bg-muted/20 border border-border/50">
                <div className="flex items-center gap-3 min-w-0">
                  <div className="w-8 h-8 rounded-full bg-accent/10 flex items-center justify-center text-sm font-bold text-accent">
                    {client.clientName.charAt(0)}
                  </div>
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-foreground truncate">{client.companyName}</p>
                    <p className="text-xs text-muted-foreground truncate">
                      {client.clientName} · <span className="capitalize">{client.plan}</span> · ₹{client.monthlyAmount}/mo
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-2 flex-shrink-0">
                  <span className="text-xs font-semibold text-success">₹{monthlyComm}/mo</span>
                  <Badge variant="outline" className={`text-[10px] ${cfg.color} flex items-center gap-1`}>
                    <StatusIcon className="h-2.5 w-2.5" /> {cfg.label}
                  </Badge>
                </div>
              </div>
            );
          })}
          {clients.length === 0 && (
            <p className="text-sm text-muted-foreground text-center py-6">No clients yet. Onboard your first client!</p>
          )}
        </div>
      </Card>

      {/* Commissions + Payouts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <PartnerCommissionTable />
        <PartnerPayoutSection />
      </div>
    </div>
  );
}
